import React, { Fragment, useState } from 'react'
import { Hidden, Grid, Chip, Box, Avatar, IconButton, MenuItem, Menu, TextField, InputAdornment, Button, useTheme, useMediaQuery, NoSsr } from '@material-ui/core'
import { Trans } from 'react-i18next'
import ArrowDropDownCircleOutlinedIcon from '@material-ui/icons/ArrowDropDownCircleOutlined'
import { useSelector, useDispatch } from 'react-redux'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import FilterListIcon from '@material-ui/icons/FilterList'
import remoteMapping from '../common/remote_mapping'
import { useUpdate, useChangeHandler } from '../reducer'
import { useStyles } from '../../pages/index'
import i18n from '../i18n/i18n'
import { pictureUrl } from '../common/pictureUrl'
import { getWilayasFromTranslation } from '../common/getWilayasFromTranslation'
import { mapByIds } from '../context/useFetch'
import APPLICATION_STATUSES from '../application_status'
import { byLabel } from '../../pages/job-offer/[pid]/apply'
import { applyFilterParentJobId } from './Fetchedmain'

const uniq = (arr) => Array.from(new Set(arr))

const filter = createFilterOptions()

const addWithSlug = (choice) => ({ ...choice, idWithType: `${choice.typeSlug}_${choice.id}` })

export const useHomeFilters = () => {
  const technologies = useSelector(({ offers, filters }) =>
    uniq(
      offers
        ? applyFilterParentJobId(offers.data, filters.parentJobId)
          .map(({ technologies }) => technologies)
          .filter(Boolean)
          .reduce((all, current) => all.concat(current), [])
          .map(({ id }) => offers.included.technologies[id])
        : []
    )
  )
  const usedWilayaIds = useSelector(({ offers, filters }) => (offers ? applyFilterParentJobId(offers.data, filters.parentJobId).map(({ company: { id } }) => offers.included.companies[id].departement_code) : []))
  const contractTypesMenu = useSelector((store) => Array.isArray(store.contractTypes) ? store.contractTypes : [])
  const levels = useSelector((store) => Array.isArray(store.levels) ? store.levels : [])
  const jobs = useSelector(({ offers, filters }) => (offers ? uniq(applyFilterParentJobId(offers.data, filters.parentJobId).map(({ job: { id } }) => offers.included.jobs[id])) : []))

  const companies = useSelector(({ offers, filters }) => uniq(offers ? applyFilterParentJobId(offers.data, filters.parentJobId).map(({ company }) => company).map(({ id }) => offers.included.companies[id]) : []))

  const allSearchOptions = () => {
    const wilayas = getWilayasFromTranslation()
      .filter(({ id }) => usedWilayaIds.includes(parseInt(id)))
      .map((wilaya) => ({
        ...wilaya,
        type: i18n.t('main.filtering.department'),
        label: wilaya.name,
        typeSlug: 'Wilaya',
        color: ''
      }))
      .map(addWithSlug)

    const contractTypes = contractTypesMenu
      .map((contract) => ({
        ...contract,
        type: i18n.t('main.contract_type'),
        typeSlug: 'ContractType',
        color: 'primary'
      }))
      .map(addWithSlug)

    const remoteChoices = remoteMapping()
      .map((remote) => ({
        ...remote,
        type: i18n.t('main.filtering.remote'),
        id: remote.slug,
        typeSlug: 'Remote',
        suffix: `${i18n.t('filtering.remote')} `,
        color: 'secondary'
      }))
      .map(addWithSlug)

    const technos = technologies
      .map((tech) => ({
        ...tech,
        type: i18n.t('main.filtering.techno'),
        typeSlug: 'Technology',
        image: 'icon',
        color: 'primary'
      }))
      .map(addWithSlug)

    const levelList = levels
      .map((level) => ({
        ...level,
        type: i18n.t('main.filtering.level'),
        typeSlug: 'Level',
        color: 'secondary'
      }))
      .map(addWithSlug)

    const jobsList = jobs
      .map((job) => ({
        ...job,
        type: i18n.t('main.filtering.job'),
        typeSlug: 'Job',
        color: 'primary'
      }))
      .map(addWithSlug)

    const companiesList = companies
      .map((company) => ({
        ...company,
        label: company.name,
        type: i18n.t('main.filtering.company'),
        typeSlug: 'Company',
        color: 'primary'
      }))
      .map(addWithSlug)

    const profile = useSelector(({ profile }) => profile)

    if (profile && profile.name && !companiesList.some((company) => company.id === profile.id)) {
      companiesList.push(
        addWithSlug({
          id: profile.id,
          label: profile.name,
          type: i18n.t('main.filtering.company'),
          typeSlug: 'Company',
          color: 'primary'
        })
      )
    }

    return {
      contractTypes,
      jobs: jobsList,
      wilayas,
      remote: remoteChoices,
      levels: levelList,
      technos,
      companies: companiesList
    }
  }

  return allSearchOptions()
}

export const HomeFiltering = () => {
  const allSearchOptions = Object.values(useHomeFilters()).reduce((sum, current) => sum.concat(current), [])

  const parentJobIds = useSelector(({ offers: { data } }) =>
    Array.from(new Set(data.map(({ parent_job }) => parent_job?.id))).filter(Boolean)
  )

  return (
    <Grid container spacing={1}>
      {parentJobIds.length > 1 &&
        <Grid item sm={4} xs={12}>
          <ParentJobFilter />
        </Grid>
      }

      <Grid item sm={parentJobIds.length < 2 ? 12 : 8} xs={12}>
        <Filtering allSearchOptions={allSearchOptions} filterKey="filters" />
      </Grid>
    </Grid>
  )
}

const ParentJobFilter = () => {
  const update = useUpdate()

  const parentJobIds = useSelector(({ offers: { data } }) =>
    Array.from(new Set(data.map(({ parent_job }) => parent_job?.id))).filter(Boolean)
  )

  const parentJobs = useSelector(({ offers: { included: { jobs } } }) =>
    parentJobIds.reduce((all, current) => {
      all.push(jobs[current])
      return all
    },
      []
    )
  )

  const filter = useSelector(({ filters }) => filters.parentJobId)

  const changeHandler = (event, value) => {
    update(['filters', 'parentJobId'], value?.id)
  }

  return (
    <Box mt={3}>
      <Autocomplete
        options={parentJobs.sort(byLabel)}
        getOptionLabel={(option) => option.label}
        filterSelectedOptions
        noOptionsText={i18n.t('noOptionsText')}
        renderInput={(params) => <TextField {...params} required variant="outlined" label={i18n.t('main.filtering.parent_job')} />}
        onChange={changeHandler}
        value={filter ? parentJobs.find(({ id }) => parseInt(id) === parseInt(filter)) : null}
      />
    </Box>
  )
}

export const useCandidatesFilters = () => {
  const offers = useSelector(({ applications }) => {
    if (!applications) {
      return []
    }
    return Object.values(applications.included.job_offers || {})
  })

  const jobs = useSelector(({ applications }) => {
    if (!applications) {
      return []
    }
    return Object.values(applications.included.jobs || {})
  })

  const technologies = useSelector(({ applications }) => {
    if (!applications) {
      return []
    }
    return Object.values(applications.included.technologies || {})
  })

  const pools = {
    true: {
      value: true,
      color: 'primary'
    }
  }

  const statuses = {
    nonSeen: {
      value: 'nonSeen',
      color: 'primary'
    }
  }

  return {
    statuses: Object.entries(APPLICATION_STATUSES)
      .map(([status, { label, icon }]) => ({
        type: i18n.t('mycandidates.filters.autocomplete.statuses'),
        typeSlug: 'Status',
        color: 'primary',
        label,
        id: status
      }))
      .map(addWithSlug),
    offers: offers
      .map((job) => ({
        ...job,
        type: i18n.t('mycandidates.filters.autocomplete.job_offer'),
        typeSlug: 'JobOffer',
        color: 'primary'
      }))
      .map(addWithSlug),
    jobs: jobs
      .map((job) => ({
        ...job,
        type: i18n.t('mycandidates.filters.autocomplete.job'),
        typeSlug: 'Job',
        color: 'primary'
      }))
      .map(addWithSlug),
    technologies: technologies
      .map((technology) => ({
        ...technology,
        type: i18n.t('mycandidates.filters.autocomplete.technologies'),
        typeSlug: 'Technology',
        color: 'secondary'
      }))
      .map(addWithSlug),
    Pool: Object.values(pools).map((pool) => ({
      color: pool.color,
      idWithType: `Pool_${pool.value}`,
      label: i18n.t('main.filtering.vivier'),
      type: 'Short-listing vivier',
      typeSlug: 'Vivier'
    })),
    Status: Object.values(statuses).map((status) => ({
      color: status.color,
      idWithType: `Status_${status.value}`,
      label: i18n.t('main.filtering.status_non_seen'),
      type: 'Short-listing status',
      typeSlug: 'Status'
    }))
  }
}

export const CandidatesFiltering = () => {
  const allSearchOptions = Object.values(useCandidatesFilters()).reduce((sum, current) => sum.concat(current), [])

  return <Filtering allSearchOptions={allSearchOptions} filterKey="candidateFilters" />
}

export const Filtering = ({ allSearchOptions, filterKey }) => {
  const update = useUpdate()

  const filters = useSelector((state) => state[filterKey]) || {}

  const selectionIds = mapByIds(allSearchOptions, 'idWithType')

  const handleToggleFilter = () => update([filterKey, 'expand'], !filters.expand)

  const searchOption = (id) => ({
    label: `${i18n.t('main.filtering.search_by')} : ${id.split('_')[1]}`,
    idWithType: id,
    id,
    type: i18n.t('main.filtering.search'),
    typeSlug: 'Search'
  })

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <Box pt={3} id="search">
      <Grid alignItems="center">
        <NoSsr>
          <Autocomplete
            fullWidth
            variant="outlined"
            noOptionsText={i18n.t('noOptionsText')}
            renderInput={(params) => (
              <TextField
                {...params}
                id="homepage-filter-field"
                autoFocus={!isMobile}
                label={i18n.t('main.search')}
                placeholder={i18n.t('main.placeholder')}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Fragment>
                      <InputAdornment position="start">
                        <Button id="homepage-filter-button" variant="contained" color={filters.expand ? '' : 'secondary'} onClick={handleToggleFilter}>
                          <Hidden xsDown>
                            <FilterListIcon />
                          </Hidden>{' '}
                          <Trans i18nKey="main.filtering.filter" />
                        </Button>
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </Fragment>
                  )
                }}
              />
            )}
            getOptionLabel={(option) => option.label}
            groupBy={(option) => option.type}
            options={allSearchOptions}
            multiple
            value={(filters.all || []).filter((value) => value !== 'Pool_false').map((id) => selectionIds[id] || searchOption(id))}
            onChange={(_event, newValue) => {
              if (document.getElementById('search').getBoundingClientRect().top !== 0) {
                location.href = '#search'
              }
              update([filterKey, 'all'], newValue.map(({ idWithType }) => idWithType).filter(Boolean))
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  color={option?.color}
                  variant="default"
                  label={`${option?.suffix || ''}${option?.label}`}
                  {...getTagProps({ index })}
                  avatar={
                    option.image && (
                      <Avatar>
                        <img src={pictureUrl({ record_type: option.typeSlug, record_id: option.id, name: option.image }, 25, 25)} />
                      </Avatar>
                    )
                  }
                />
              ))
            }
            freeSolo
            filterOptions={(options, params) => {
              const filtered = filter(options, params)

              // Suggest the creation of a new value
              if (params.inputValue !== '') {
                filtered.push({
                  idWithType: `Search_${params.inputValue}`,
                  inputValue: params.inputValue,
                  label: `${i18n.t('main.filtering.search_by')} "${params.inputValue}"`
                })
              }

              return filtered
            }}
          />
        </NoSsr>
      </Grid>
    </Box>
  )
}

const CHOICES_LIMIT = 10

export const Choices = ({ choices, showAll, filterKey = 'filters' }) => {
  const [showMore, setShowMore] = useState(false)

  const update = useUpdate()

  const filters = useSelector((state) => state[filterKey].all || [])

  const isChecked = (choice) => filters.includes(choice.idWithType)
  const handleToggleClick = (choice) =>
    isChecked(choice)
      ? update(
        [filterKey, 'all'],
        filters.filter((idWithType) => idWithType !== choice.idWithType)
      )
      : update([filterKey, 'all'], [...filters, choice.idWithType])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const handleClick = (choice) => () => {
    handleToggleClick(choice)
    location.href = '#search'

    if (isMobile) {
      update([filterKey, 'expand'], false)
    }
  }

  const isAllChecked = () => choices.every((choice) => isChecked(choice)) || choices.every((choice) => !isChecked(choice))

  const handleAllClick = () => {
    if (!isAllChecked() && choices[0]) {
      update(
        [filterKey, 'all'],
        filters.filter((filter) => !filter.startsWith(`${choices[0].typeSlug}_`))
      )
    }
  }

  const showingChoices = showMore ? choices : choices.slice(0, CHOICES_LIMIT)

  return (
    <Box mt={1} mb={3}>
      {showAll && <Chip label={i18n.t('main.filtering.all')} color={isAllChecked() ? 'secondary' : 'inherit'} style={{ margin: 1 }} onClick={handleAllClick} />}

      {showingChoices.map((choice) => (
        <Chip label={choice.label} color={isChecked(choice) ? 'secondary' : 'inherit'} style={{ margin: 1 }} onClick={handleClick(choice)} />
      ))}

      {showingChoices.length < choices.length && (
        <Button variant="outlined" color="secondary" size="small" onClick={() => setShowMore(true)}>
          <Trans i18nKey="main.filtering.show_more" />
        </Button>
      )}

      {showMore && (
        <Button variant="outlined" color="secondary" size="small" onClick={() => setShowMore(false)}>
          <Trans i18nKey="main.filtering.show_less" />
        </Button>
      )}
    </Box>
  )
}

// Returns object of filter types => ids from array of FilterType_123
export const groupFilters = (idsWithTypes) =>
  idsWithTypes
    .map((idWithType) => {
      const [id, ...parts] = idWithType.split('_')
      return [id, parts.join('_')]
    })
    .reduce((all, [type, id]) => {
      all[type] = [...(all[type] || []), id]
      return all
    }, {})